import { ChartBarIcon, ShoppingBagIcon, UsersIcon, CurrencyDollarIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline";
import { fetchDashboardDetail, fetchUserOrders } from "../services/user";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, ClockIcon, TruckIcon, ClockArrowUpIcon, BanIcon, Contact, Contact2Icon, SparklesIcon } from "lucide-react";
import axios from 'axios';
import { AIInsightsModal } from "../components/Dashboard/AIInsightsModal";
import { fetchAIInsights } from "../services/user";
import { useTheme } from "../context/ThemeContext";

const Dashboard: React.FC = () => {
  const { role } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('All Time');
  const [orderMetrics, setOrderMetrics] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [insightsData, setInsightsData] = useState<string>('');
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const navigate = useNavigate();

  // Map status to icons
  const statusIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'Completed': CheckCircleIcon,
    'In-progress': TruckIcon,
    'Pending': ClockIcon,
    'Accepted': ClockArrowUpIcon,
    'Cancelled': BanIcon
  };

  useEffect(() => {
    const userId = localStorage.getItem('ud-user') ?? '';
    setIsLoading(true);
    const fetchDashboardDetails = async () => {
      try {
        setError(null);
        // Assuming fetchDashboardDetail from services needs to be bypassed or it accepts dateRange.
        // Better to call axios directly to pass dateRange
        const baseUrl = process.env.REACT_APP_API_URL || 'https://seller.udyamwell.in/api';
        const response = await axios.get(`${baseUrl}/dashboard/stats`, {
          params: { userId, dateRange }
        });
        setOrderMetrics(response.data);

        // Also set recent orders from the recentActivities if you want, or keep fetchUserOrders
        if (response.data.recentActivities) {
          setRecentOrders(response.data.recentActivities);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardDetails();
  }, [dateRange]);

  const quickActions = [
    { name: 'Add New Product', href: '/products/add', icon: ShoppingBagIcon },
    { name: 'View Orders', href: '/orders', icon: ChartBarIcon },
    { name: 'Contact Us', href: '/contact-us', icon: Contact2Icon },
    { name: 'Seller Insights', href: '/account', icon: UsersIcon },
  ];

  const metrics: any = orderMetrics || {};

  const handleGenerateInsights = async () => {
    setIsInsightsModalOpen(true);
    if (!insightsData) {
      setIsInsightsLoading(true);
      try {
        const response = await fetchAIInsights(metrics);
        if (response.response) {
          setInsightsData(response.response);
        } else {
          setInsightsData('Failed to generate insights.');
        }
      } catch (err) {
        setInsightsData('An error occurred while communicating with the AI.');
      } finally {
        setIsInsightsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">Overview of your store performance and activity</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          {role === 'ORGANISATION' && (
            <button
              onClick={handleGenerateInsights}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium shadow-sm py-2 px-4 rounded-md hover:from-indigo-600 hover:to-purple-700 transition-all"
            >
              <SparklesIcon size={18} />
              Generate AI Insights
            </button>
          )}
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value)}
            className="bg-white border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
          >
            <option>All Time</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>This Quarter</option>
            <option>Year to Date</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      ) : (
        <>
          {/* Top Level KPI Cards */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg p-5 flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-green-500 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-500 truncate">Total Revenue</p>
                <p className="text-xl font-bold text-gray-900">₹{metrics.totalRevenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg p-5 flex items-center">
              <ShoppingBagIcon className="h-8 w-8 text-blue-500 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-500 truncate">Total Orders</p>
                <p className="text-xl font-bold text-gray-900">{metrics.totalOrders || 0}</p>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg p-5 flex items-center">
              <ChartBarIcon className="h-8 w-8 text-purple-500 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-500 truncate">Average Order Value</p>
                <p className="text-xl font-bold text-gray-900">₹{metrics.averageOrderValue || 0}</p>
              </div>
            </div>
            {metrics.sellersCount > 1 && (
              <div className="bg-white overflow-hidden shadow rounded-lg p-5 flex items-center">
                <UsersIcon className="h-8 w-8 text-orange-500 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 truncate">Active / Total Sellers</p>
                  <p className="text-xl font-bold text-gray-900">{metrics.activeSellers || 0} / {metrics.sellersCount}</p>
                </div>
              </div>
            )}
          </div>

          <AIInsightsModal
            isOpen={isInsightsModalOpen}
            onClose={() => setIsInsightsModalOpen(false)}
            insights={insightsData}
            isLoading={isInsightsLoading}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

            {/* Recent Activities */}
            <div className="bg-white shadow rounded-lg lg:col-span-2 p-6 flex flex-col">
              <h3 className="text-lg font-bold leading-6 text-gray-900 mb-4 border-b pb-2">Recent Activities</h3>
              {recentOrders.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No recent activity found.</div>
              ) : (
                <div className="space-y-4 flex-1">
                  {recentOrders.map((activity: any, idx: number) => {
                    const Icon = activity.activity.includes('Pending') ? ClockIcon : CheckCircleIcon;
                    return (
                      <div key={idx} className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <Icon className={`w-5 h-5 ${activity.activity.includes('Pending') ? 'text-yellow-500' : 'text-green-500'}`} />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{activity.details}</p>
                          <p className="text-xs text-gray-500">{new Date(activity.date).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 pt-4 border-t text-sm font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer" onClick={() => navigate('/orders')}>
                View all orders &rarr;
              </div>
            </div>

            {/* Top Lists */}
            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-bold leading-6 text-gray-900 mb-4 border-b pb-2">Top Selling Products</h3>
                <div className="space-y-3">
                  {metrics.topProducts && metrics.topProducts.length > 0 ? metrics.topProducts.map((p: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center text-sm relative group cursor-pointer">
                      <span className="text-gray-700 truncate mr-2">{p.name}</span>
                      <span className="font-semibold whitespace-nowrap">₹{p.revenue.toFixed(2)}</span>
                      {/* Custom Tooltip */}
                      <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-normal max-w-xs break-words shadow-lg">
                        {p.name}
                      </div>
                    </div>
                  )) : <p className="text-gray-500 text-sm">No data available.</p>}
                </div>
              </div>

              {metrics.sellersCount > 1 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-bold leading-6 text-gray-900 mb-4 border-b pb-2">Top Performing Sellers</h3>
                  <div className="space-y-3">
                    {metrics.topSellers && metrics.topSellers.length > 0 ? metrics.topSellers.map((s: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center text-sm relative group cursor-pointer">
                        <span className="text-gray-700 truncate mr-2">{s.name}</span>
                        <span className="font-semibold whitespace-nowrap">₹{s.revenue.toFixed(2)}</span>
                        {/* Custom Tooltip */}
                        <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-normal break-words max-w-[250px] shadow-lg">
                          {s.name}
                        </div>
                      </div>
                    )) : <p className="text-gray-500 text-sm">No data available.</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions & Pending Tasks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-bold leading-6 text-gray-900 mb-4 border-b pb-2">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {quickActions.map((action) => (
                  <a
                    key={action.name}
                    href={action.href}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm flex items-center space-x-3 hover:border-green-500 transition"
                  >
                    <action.icon className="h-6 w-6 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{action.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6 flex flex-col justify-center items-center text-center">
              <ClockIcon className="w-12 h-12 text-yellow-500 mb-2" />
              <h3 className="text-2xl font-bold text-gray-900">{metrics.pendingTasks || 0}</h3>
              <p className="text-gray-600 font-medium">Pending Orders / Tasks</p>
              <button onClick={() => navigate('/orders')} className="mt-4 px-4 py-2 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded-md text-sm font-semibold transition">
                Review Pending
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;